import axios from "axios";
import { getSupabase } from "@/lib/supabaseClient";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

async function authHeader() {
  const client = getSupabase();
  if (!client) return {};
  const { data } = await client.auth.getSession();
  const token = data?.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function req(method, path, body, extraConfig = {}) {
  const headers = { "Content-Type": "application/json", ...(await authHeader()) };
  const resp = await axios({
    method,
    url: `${API}${path}`,
    data: body,
    headers,
    timeout: 180000,
    ...extraConfig,
  });
  return resp.data;
}

export const api = {
  health: () => req("get", "/health"),
  getProfile: () => req("get", "/profile"),
  updateProfile: (patch) => req("put", "/profile", patch),
  listSyllabi: () => req("get", "/syllabi"),
  saveSyllabus: (payload) => req("post", "/syllabi", payload),
  generate: (payload) => req("post", "/generate", payload),
  listDocuments: (archived = false) => req("get", "/documents", undefined, { params: { archived } }),
  getDocument: (id) => req("get", `/documents/${id}`),
  patchDocument: (id, patch) => req("patch", `/documents/${id}`, patch),
  unarchiveDocument: (id) => req("post", `/documents/${id}/unarchive`),
  getPublicTemplate: (slug) => req("get", `/public/templates/${slug}`),
  listPublicTemplates: () => req("get", "/public/templates"),
  exportDocumentUrl: async (id, fmt, style = "auto") => {
    const headers = await authHeader();
    const resp = await axios({
      method: "get",
      url: `${API}/documents/${id}/export`,
      params: { format: fmt, style },
      headers,
      responseType: "blob",
      timeout: 180000,
    });
    const disposition = resp.headers?.["content-disposition"] || "";
    const m = /filename="?([^"]+)"?/i.exec(disposition);
    const filename = m ? m[1] : `pocket-ta-ai-document.${fmt}`;
    return { blob: resp.data, filename };
  },
  emailExportDocument: (id, payload) => req("post", `/documents/${id}/email-export`, payload),
  syncDriveDocument: (id, payload) => req("post", `/documents/${id}/sync-drive`, payload),
  emailDocument: (payload) => req("post", "/email/send", payload),
  submitFeedback: (payload) => req("post", "/feedback", payload),
  briefingToday: () => req("get", "/briefing/today"),
  bindReferral: (referrer_user_id) => req("post", "/referral/bind", { referrer_user_id }),
  stripeSubscription: (origin) => req("post", "/stripe/checkout/subscription", { origin_url: origin }),
  stripeTopup: (origin) => req("post", "/stripe/checkout/topup", { origin_url: origin }),
  stripePortal: (origin) => req("post", "/stripe/portal", { origin_url: origin }),
  stripeVerifySession: (session_id) => req("post", "/stripe/verify-session", { session_id }),
  adminMetrics: () => req("get", "/admin/metrics"),
  adminUsers: () => req("get", "/admin/users"),
  adminAdjust: (payload) => req("post", "/admin/adjust", payload),
};

export const i18n = {
  en: {
    appName: "POCKET TA AI",
    tagline: "LEVERAGING AI FOR THE REST OF US",
    welcome: "Welcome to POCKET TA AI",
    login: "Log In",
    register: "Create Account",
    email: "Email",
    password: "Password",
    forgotPassword: "Forgot Password?",
    forgotUserId: "Forgot User ID?",
    showPassword: "Show password",
    hidePassword: "Hide password",
    continue: "I Understand - Continue",
    disclosureFooter: "By continuing, you acknowledge that you have read and understood this disclosure.",
    workspace: "Workspace",
    vault: "Vault Library",
    profile: "Profile Hub",
    syllabus: "Course Syllabus",
    briefing: "Daily Morning Briefing",
    submit: "Generate",
    submitting: "AI is thinking and actively researching current web directories via Google Search Grounding",
    creditCost: "Credit cost",
    baseCredits: "Base credits",
    topupCredits: "Top-up credits",
    addTopup: "Buy 300 Credits ($10)",
    manageBilling: "Manage Billing",
    subscribe: "Subscribe ($19.99 / month)",
    subscriptionInactive: "Subscription inactive. New document creation is paused. Upgrade your plan to unlock full AI generation tools.",
    offlineMessage: "Network connection lost. Please check your signal. POCKET TA AI will automatically resume synchronization once online.",
    listen: "Listen",
    stopListen: "Stop",
    sendEmail: "Send to Email",
    exportAs: "Download As",
    emailAs: "Email As Attachment",
    exportPdf: "PDF",
    exportDocx: "Word (.docx)",
    exportPptx: "PowerPoint (.pptx)",
    cancel: "Cancel",
    exporting: "Preparing download...",
    sendingEmail: "Sending email...",
    emailSent: "Email sent",
    emailFailed: "Email failed",
    chooseFormat: "Choose a format",
    emailTo: "Recipient email",
    designStyle: "Design style (PowerPoint)",
    styleAuto: "Automatic (AI chooses)",
    styleEditorialLight: "Editorial Light",
    styleMidnight: "Midnight",
    styleHighContrast: "High-Contrast",
    syncDrive: "Sync to Google Drive",
    syncingDrive: "Syncing to Google Drive...",
    syncedDrive: "Synced to Google Drive",
    openInDrive: "Open in Google Drive",
    showArchived: "Show Archived Items",
    hideArchived: "Hide Archived Items",
    archivedEmpty: "No archived documents.",
    unarchive: "Unarchive",
    unarchiving: "Restoring...",
    unarchived: "Restored to your Vault",
    archivedHeading: "Archived Documents",
    archivePolicy: "Archived items are permanently deleted after 30 days.",
    sharedTemplateLabel: "Shared Department Template",
    publicViewPage: "View public page",
    publicTemplateFooter: "Shared from",
    publicTemplateBy: "A department template shared on POCKET TA AI.",
    templateNotFound: "This template is no longer shared or does not exist.",
    templateError: "We could not load this template. Please try again later.",
    backToHome: "Go to POCKET TA AI",
    loading: "Loading...",
    galleryTitle: "Department Template Gallery",
    gallerySubtitle: "Browse academic templates shared by professors on POCKET TA AI - lesson plans, rubrics, exams and more. Open any template to read it in full.",
    galleryEmpty: "No public templates have been shared yet.",
    galleryError: "We could not load the template gallery. Please try again later.",
    viewTemplate: "View Template",
    createAccount: "Create Your Own Account",
    share: "Share Resource with Colleague",
    rename: "Rename File",
    publishTemplate: "Publish as Department Template",
    feedbackUp: "Useful",
    feedbackDown: "Not useful",
    reportIssue: "Report an Issue",
    logout: "Sign Out",
    universityName: "University Name",
    departmentProgram: "Department / Program",
    certifications: "Certifications & Credentials",
    workExperience: "Work & Teaching Experience",
    specializations: "Professional Specializations",
    proxySettings: "Library Proxy Settings",
    anythingElse: "Anything Else Box",
    save: "Save",
    saved: "Saved",
    durationMinutes: "Duration (minutes)",
    admin: "Admin Portal",
    citations: "References",
    addSyllabus: "New Syllabus",
    syllabusTitle: "Course Title",
    syllabusSemester: "Semester Label",
    syllabusText: "Syllabus Text",
    connecting: "Connecting to POCKET TA AI services",
  },
  es: {
    appName: "POCKET TA AI",
    tagline: "APROVECHANDO LA IA PARA EL RESTO DE NOSOTROS",
    welcome: "Bienvenido a POCKET TA AI",
    login: "Iniciar Sesion",
    register: "Crear Cuenta",
    email: "Correo",
    password: "Contrasena",
    forgotPassword: "Olvide mi contrasena",
    forgotUserId: "Olvide mi ID de usuario",
    showPassword: "Mostrar contrasena",
    hidePassword: "Ocultar contrasena",
    continue: "Entiendo - Continuar",
    disclosureFooter: "Al continuar, reconoces que has leido y entendido esta divulgacion.",
    workspace: "Espacio de Trabajo",
    vault: "Biblioteca",
    profile: "Perfil",
    syllabus: "Programa del Curso",
    briefing: "Resumen Matutino",
    submit: "Generar",
    submitting: "La IA esta pensando y consultando activamente los directorios web actuales via Google Search Grounding",
    creditCost: "Costo en creditos",
    baseCredits: "Creditos base",
    topupCredits: "Creditos recarga",
    addTopup: "Comprar 300 Creditos ($10)",
    manageBilling: "Administrar Facturacion",
    subscribe: "Suscribirse ($19.99 / mes)",
    subscriptionInactive: "Suscripcion inactiva. La creacion de nuevos documentos esta pausada. Actualiza tu plan para desbloquear las herramientas.",
    offlineMessage: "Conexion de red perdida. Por favor verifica tu senal. POCKET TA AI reanudara la sincronizacion automaticamente al volver en linea.",
    listen: "Escuchar",
    stopListen: "Detener",
    sendEmail: "Enviar por correo",
    exportAs: "Descargar como",
    emailAs: "Enviar como adjunto",
    exportPdf: "PDF",
    exportDocx: "Word (.docx)",
    exportPptx: "PowerPoint (.pptx)",
    cancel: "Cancelar",
    exporting: "Preparando descarga...",
    sendingEmail: "Enviando correo...",
    emailSent: "Correo enviado",
    emailFailed: "Fallo el envio",
    chooseFormat: "Elige un formato",
    emailTo: "Correo destinatario",
    designStyle: "Estilo de diseno (PowerPoint)",
    styleAuto: "Automatico (la IA elige)",
    styleEditorialLight: "Editorial Claro",
    styleMidnight: "Medianoche",
    styleHighContrast: "Alto Contraste",
    syncDrive: "Sincronizar con Google Drive",
    syncingDrive: "Sincronizando con Google Drive...",
    syncedDrive: "Sincronizado con Google Drive",
    openInDrive: "Abrir en Google Drive",
    showArchived: "Mostrar archivados",
    hideArchived: "Ocultar archivados",
    archivedEmpty: "No hay documentos archivados.",
    unarchive: "Desarchivar",
    unarchiving: "Restaurando...",
    unarchived: "Restaurado a tu biblioteca",
    archivedHeading: "Documentos Archivados",
    archivePolicy: "Los elementos archivados se eliminan permanentemente despues de 30 dias.",
    sharedTemplateLabel: "Plantilla de departamento compartida",
    publicViewPage: "Ver pagina publica",
    publicTemplateFooter: "Compartido desde",
    publicTemplateBy: "Una plantilla de departamento compartida en POCKET TA AI.",
    templateNotFound: "Esta plantilla ya no esta compartida o no existe.",
    templateError: "No pudimos cargar esta plantilla. Intentalo mas tarde.",
    backToHome: "Ir a POCKET TA AI",
    loading: "Cargando...",
    galleryTitle: "Galeria de plantillas de departamento",
    gallerySubtitle: "Explora plantillas academicas compartidas por profesores en POCKET TA AI: planes de clase, rubricas, examenes y mas. Abre cualquier plantilla para leerla completa.",
    galleryEmpty: "Aun no se han compartido plantillas publicas.",
    galleryError: "No pudimos cargar la galeria de plantillas. Intentalo mas tarde.",
    viewTemplate: "Ver plantilla",
    createAccount: "Crea tu propia cuenta",
    share: "Compartir con Colega",
    rename: "Renombrar Archivo",
    publishTemplate: "Publicar como Plantilla Departamental",
    feedbackUp: "Util",
    feedbackDown: "No util",
    reportIssue: "Reportar un Problema",
    logout: "Cerrar Sesion",
    universityName: "Nombre de la Universidad",
    departmentProgram: "Departamento / Programa",
    certifications: "Certificaciones y Credenciales",
    workExperience: "Experiencia Laboral y Docente",
    specializations: "Especializaciones Profesionales",
    proxySettings: "Credenciales del Proxy de Biblioteca",
    anythingElse: "Cualquier Otro Contexto",
    save: "Guardar",
    saved: "Guardado",
    durationMinutes: "Duracion (minutos)",
    admin: "Panel de Administracion",
    citations: "Referencias",
    addSyllabus: "Nuevo Programa",
    syllabusTitle: "Titulo del Curso",
    syllabusSemester: "Etiqueta del Semestre",
    syllabusText: "Texto del Programa",
    connecting: "Conectando con los servicios de POCKET TA AI",
  },
};

export const MODULES = [
  { id: 1, en: "Master Tutor", es: "Tutor Maestro" },
  { id: 2, en: "Lesson Plan Builder", es: "Plan de Leccion" },
  { id: 3, en: "Slide Architect", es: "Diapositivas" },
  { id: 4, en: "Exam Generator", es: "Examenes" },
  { id: 5, en: "Study Guides", es: "Guias de Estudio" },
  { id: 6, en: "Mistake Fixer", es: "Reparar Errores" },
  { id: 7, en: "Media Analyzer", es: "Analisis de Medios" },
  { id: 8, en: "Essay Grader", es: "Evaluar Ensayos" },
  { id: 9, en: "Email Reply", es: "Respuesta de Correo" },
  { id: 10, en: "Recommendation Letter", es: "Carta de Recomendacion" },
];
